import {ConfigService} from "../../services/ConfigService";
import {ErrorService} from "../../services/ErrorService";
import {SeoService} from "../../services/SeoService";
import {Component} from "../../components/Component";
import {AnalyticsService} from "../../services/AnalyticsService";
import {LinkParams} from "../../services/LinkService";
import {PageStatus} from "../../definitions/CommonDefinitions";
import {createElement, createFragment, setVirtualDOM} from "../../components/Templates";
import {Router, RequestDetails} from "../../utils/Router";

// Hack to register custom elements for all CustomElements (ie not extending Component)
// The @element decorator doesn't work with ES6 styles and webpack
import * as PriceElement from "../../components/PriceElement";
import * as RatingElement from "../../components/RatingElement";
import * as FlyerViewElement from "../../components/flipp-flyerview/FlyerViewElement";

let nowOffset = Date.now();

//
// Where to put? Note for iso this would be different on the server.
let currentComponent: Component<any, any> = null;

// init
const dom = document.getElementById("app");
const initialHTML = document.getElementById("initial-html");
const analytics = AnalyticsService.getInstance();
const config = ConfigService.getInstance();

// Report render time to console
const TIME_RENDER = false;


// Public Definitions ------------------------------------------------------->

function getState() {
	/* Terrible workaround for IE11 where sometimes window.history.state returns "Permission Denied"
	 * error and the Error page is displayed to the user. In these cases, just default to empty state. */
	let state: any;
	try {
		state = window.history.state;
	} catch (e) {
		state = {};
	}
	return state;
}

function now() {
	if (window.performance && window.performance.now)
		return window.performance.now();

	return Date.now() - nowOffset;
}

export function init() {
	// Hack to assign functions to global scope
	window.flipp = {
		now,
		devicePixelRatio: 0,
		createElement,
		createFragment,
		setVirtualDOM,
		status
	};
	Object.defineProperty(flipp, "status", {
		enumerable: true,
		get: function() {
			return (flipp as any)._status || PageStatus.Loading;
		},
		set: function(v: PageStatus) {
			const notify = (flipp as any)._status !== v && v === PageStatus.Loaded;
			(flipp as any)._status = v;
			if (notify) {
				ErrorService.getInstance().siteLoaded();
				analytics.siteLoaded();
			}
		}
	});

	PriceElement.register();
	RatingElement.register();
	FlyerViewElement.register();

	// Polyfill window.performance on Safari 8.
	if (window.performance.timing && window.performance.timing.navigationStart)
		nowOffset = window.performance.timing.navigationStart;

	document.addEventListener("DOMContentLoaded", function() {
		// Test whether a 0.5 pixel border is supported (Safari 8+) for hairline
		// borders on retina screens.
		if (window.devicePixelRatio && devicePixelRatio >= 2) {
			const el = document.createElement("div");
			el.style.border = "0.5px solid transparent";
			document.body.appendChild(el);
			if (el.offsetHeight === 1)
				document.documentElement.setAttribute("hairlines", "");
			document.body.removeChild(el);
		}

		if (document.body.style.hasOwnProperty("objectFit") &&
				document.body.style.hasOwnProperty("objectPosition"))
			document.documentElement.setAttribute("objectfit", "");

		if (navigator.userAgent.match(/iPhone/) ||
				navigator.userAgent.match(/iPad/))
			document.documentElement.setAttribute("ios", "");

		if (navigator.userAgent.match(/Android/) ||
				navigator.userAgent.match(/iPhone/) ||
				navigator.userAgent.match(/iPad/))
			document.documentElement.setAttribute("touchscreen", "");
	});


	// Poll for device pixel ratio ranges and fire an event. This happens when
	// moving a window across monitors of different pixel densitiy (e.g. a 4k
	// screen and a normal screen), or when changing the browser zoom level.
	function pollDevicePixelRatio() {
		const clampedRatio = Math.min(4, window.devicePixelRatio);
		if (clampedRatio !== flipp.devicePixelRatio) {
			flipp.devicePixelRatio = clampedRatio;
			window.dispatchEvent(new Event("flipp-devicepixelratiochange"));
		}
	}
	pollDevicePixelRatio();
	window.setInterval(pollDevicePixelRatio, 5000);

	// Fiddle with the focus outline when user tabs versus clicks
	document.documentElement.addEventListener("keydown", (e) => {
		if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey)
			return;

		const el = document.activeElement;
		if (el instanceof HTMLInputElement && e.keyCode !== 9) {
			if (el.type !== "search" && el.type !== "text")
				document.documentElement.removeAttribute("hide-focus-outline");
		} else {
			document.documentElement.removeAttribute("hide-focus-outline");
		}
	}, true);

	const handlePointerDown = () => {
		document.documentElement.setAttribute("hide-focus-outline", "");
	};
	document.documentElement.addEventListener("pointerdown", handlePointerDown, true);
	document.documentElement.addEventListener("mousedown", handlePointerDown, true);
	document.documentElement.addEventListener("touchstart", handlePointerDown, true);
}

export function go(router: Router) {
	function handleLocation(url: string, state: any, params: LinkParams = {}) {
		// clear content
		// dom.innerHTML = '';
		const renderStart = new Date().getTime();
		const reqDetails: RequestDetails = {
			sourceAction: params.sourceAction,
			context: params.context
		};

		// load new url
		router.handleURL(new URL(url, window.location.href), reqDetails).then((res) => {
			if (res.status === 302 || res.status === 301) {
				// Use an HTMLAnchorElement to convert a potential relative url into an
				// absolute one.
				const anchor = document.createElement("a");
				anchor.href = res.headers["Location"];

				if (anchor.href === document.location.href) {
					throw new Error("Infinite redirect");
				}

				// set the url and handle it
				history.replaceState(state, "", anchor.href);
				handleLocation(anchor.href, state);
				return;
			}

			if (res.title && config.PUSHSTATE_SUPPORTED) {
				Promise.resolve(res.title).then((title) => {
					// put the title in
					history.replaceState({}, title, url);

					document.title = title;

					// Send the page view. Wait until res.title is resolved (if a promise)
					analytics.trackPageView();
				});
			} else {
				// Send the page view
				analytics.trackPageView();
			}

			if (res.description) {
				Promise.resolve(res.description).then((description) => {
					SeoService.getInstance().setDescription(description);
				});
			}

			function append() {
				if (TIME_RENDER) console.log("[render]", new Date().getTime() - renderStart);
				if (res.body) {
					initialHTML.style.display = "none";
					while (dom.firstChild) dom.removeChild(dom.firstChild);
					dom.appendChild(res.body);
				}

				if (document.body.classList.contains("loading")) {
					document.body.classList.remove("loading");
				}

				if (TIME_RENDER) console.log("[append]", new Date().getTime() - renderStart);
			}

			// 'resolve' the response, this allows post render action to happen...
			if (res.body && "COMPONENT_STYLE_CREATE_ELEMENT" in res.body) {
				const component = (res.body as any) as Component<any, any>;
				currentComponent = component;

				component.restore(state);
				component.invalidate()
						.then(() => {
							append();
							if (flipp.status === PageStatus.Loading) {
								flipp.status = PageStatus.Loaded;
							}
							res.resolve();
						}).catch((e: Error) => {
							ErrorService.getInstance().reportError(e);
						});
			} else {
				append();
				if (flipp.status === PageStatus.Loading) {
					flipp.status = PageStatus.Loaded;
				}
				res.resolve();
			}
		}).catch((err: Error) => {
			ErrorService.getInstance().reportError(err);
		});
	}

	const unsupported = document.documentElement.getAttribute("unsupported") === "true";
	if (unsupported) {
		/* If we're in an 'unsupported' case (distribution URL with older browser), then just return */
		return;
	}

	window.addEventListener("flipp-navigate", (e: CustomEvent) => {
		try {
			if (config.PUSHSTATE_SUPPORTED) {
				// save state if there is any
				if (currentComponent) {
					history.replaceState(currentComponent.state, "", window.location.href);
				}

				if (e.detail.params && e.detail.params.tracked === false) {
					history.replaceState({}, "", e.detail.href);
				} else {
					if (e.detail.params.hostname !== window.location.hostname) {
						window.location.href = `${window.location.protocol}//${e.detail.params.hostname}${e.detail.href}`;
					} else if (e.detail.href && e.detail.href[0] !== "/" && e.detail.href.includes(window.location.protocol)) {
						window.location.href = `${e.detail.href}`;
					} else {
						history.pushState({}, "", e.detail.href);
					}
				}
			} else {
				const historyState = getState();
				let prev = [];
				if (Array.isArray(historyState)) {
					prev = historyState;
				}
				prev.push(window.location.href);
				history.replaceState(prev, "", e.detail.href);
			}
			handleLocation(window.location.href, null, e.detail.params);
		} catch (e) {
			console.log(e);
		}
	});

	// listen to popstate
	window.addEventListener("popstate", (e) => {
		e.preventDefault();

		// restore the state if there is one...
		handleLocation(window.location.href, e.state);
	});

	window.addEventListener("beforeunload", (e) => {
		// save state
		if (currentComponent && config.PUSHSTATE_SUPPORTED) {
			history.replaceState(currentComponent.state, "", window.location.href);
		}
	});

	// somehow this is more stable than window.addEventListener("beforeunload")
	window.onbeforeunload = function(e) {
		// send closing event for latest open flyer
		analytics.trackWindowClose();
	};

	// kick it off
	handleLocation(window.location.href, getState());
}
